import { openDB } from 'idb';
import { useState, useEffect } from 'react';

/**
 * Simple storage handler
 */ const simpleStorageHandler = {
    /**
   * @inheritdoc
   */ initialize (database, storeName) {
        database.createObjectStore(storeName);
    },
    /**
   * @inheritdoc
   */ upgrade (database, storeName) {
        database.deleteObjectStore(storeName);
        this.initialize(database, storeName);
    },
    /**
   * @inheritdoc
   */ replace: (_key, value)=>value,
    /**
   * @inheritdoc
   */ revive: (_key, storeObject)=>storeObject
};

/**
 * Cache provider factory
 */ async function createCacheProvider({ dbName, storeName, storageHandler = simpleStorageHandler, version = 1, onError = ()=>{} }) {
    // Initialize storage snapshot
    const map = new Map();
    // Initialize database
    let db;
    try {
        db = await openDB(dbName, version, {
            upgrade (upgradeDb, oldVersion) {
                if (!oldVersion) {
                    storageHandler.initialize(upgradeDb, storeName);
                } else {
                    storageHandler.upgrade(upgradeDb, storeName, oldVersion);
                }
            }
        });
        // Get storage snapshot
        let cursor = await db.transaction(storeName, 'readwrite').store.openCursor();
        while(cursor){
            const key = cursor.key;
            const value = storageHandler.revive(key, cursor.value);
            // Stale
            if (value === undefined) {
                cursor.delete();
            // OK
            } else {
                map.set(key, value);
            }
            cursor = await cursor.continue();
        }
    } catch (error) {
        // Use fallback
        onError(error);
        return ()=>map;
    }
    /**
   * SWR Cache provider API
   */ return (_globalCache)=>({
            keys: ()=>map.keys(),
            get: (key)=>map.get(key),
            set: (key, value)=>{
                map.set(key, value);
                if (isFetchInfo(value)) {
                    return;
                }
                const storeValue = storageHandler.replace(key, value);
                if (storeValue === undefined) {
                    return;
                }
                db.put(storeName, storeValue, key).catch(onError);
            },
            /**
     * Used only by useSWRInfinite
     */ delete: (key)=>{
                if (map.delete(key)) {
                    db.delete(storeName, key).catch(onError);
                }
            },
            /**
     * Documented, but missing method type
     * @link https://swr.vercel.app/docs/advanced/cache#access-to-the-cache
     * @link https://github.com/vercel/swr/pull/1936
     */ // @ts-ignore
            clear: ()=>{
                map.clear();
                db.clear(storeName);
            }
        });
    /**
   * Do not store as non-native errors are not serializable, other properties are optional
   * @link https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm#supported_types
   */ function isFetchInfo(state) {
        return state.error instanceof Error || state.isValidating === true || state.isLoading === true;
    }
}

/**
 * Cache provider hook
 */ function useCacheProvider(props) {
    const [cacheProvider, setCacheProvider] = useState();
    useEffect(()=>{
        // False on mount or on dependency change
        let isSetup = true;
        createCacheProvider(props).then((cp)=>isSetup && setCacheProvider(()=>cp));
        return ()=>{
            isSetup = false;
        };
    }, []);
    return cacheProvider;
}

/**
 * Storage value handler that wraps value in object containing timestamp
 */ const timestampStorageHandler = {
    ...simpleStorageHandler,
    /**
   * @inheritdoc
   */ initialize (database, storeName) {
        const objectStore = database.createObjectStore(storeName);
        objectStore.createIndex('ts', 'ts');
    },
    /**
   * @inheritdoc
   */ replace: (_key, value)=>({
            value,
            ts: Date.now()
        }),
    /**
   * @inheritdoc
   */ revive: (_key, storeObject)=>storeObject.value
};

export { createCacheProvider as default, simpleStorageHandler, timestampStorageHandler, useCacheProvider };
